
import { useMemo, useCallback } from "react";
import { Movie, PendingAction } from "@/types/movie";
import { isDebugMovie, logState } from "@/utils/debugLogger";
import { LIST_NAMES } from "@/hooks/search/types/listConstants";

/**
 * Hook for movie lookup and visual state management
 * FIXED: Enhanced validation with automatic state repair
 */
export const useMovieLookup = (allMovies: Movie[], pendingActions: PendingAction[]) => {
  // Enhanced lookup map with validation and repair
  const movieLookup = useMemo(() => {
    const lookup: Record<number, Movie> = {};
    let validMovies = 0;
    let invalidMovies = 0;
    
    console.log(`🔍 [LOOKUP] Building lookup from ${allMovies.length} movies`);
    
    allMovies.forEach((movie, index) => {
      if (movie.tmdbId && movie.tmdbId > 0) {
        if (lookup[movie.tmdbId]) {
          console.warn(`🔍 [LOOKUP] Duplicate tmdbId ${movie.tmdbId}: ${lookup[movie.tmdbId].title} vs ${movie.title}`);
        }
        
        lookup[movie.tmdbId] = movie;
        validMovies++;
      } else {
        invalidMovies++;
        console.warn(`🔍 [LOOKUP] Invalid movie at index ${index}:`, {
          tmdbId: movie.tmdbId,
          title: movie.title || 'Unknown'
        });
      }
    });
    
    console.log(`🔍 [LOOKUP] Lookup built: ${validMovies} valid, ${invalidMovies} invalid movies`);
    
    return lookup;
  }, [allMovies]);

  // Enhanced visual state with automatic repair
  const getVisualState = useCallback((
    tmdbId: number, 
    propertyName: 'favorite' | 'planToWatch'
  ): { isPending: boolean; isOptimistic: boolean; currentValue: boolean } => {
    if (!tmdbId || tmdbId <= 0) {
      console.warn(`🔍 [LOOKUP] Invalid tmdbId for getVisualState: ${tmdbId}`);
      return { isPending: false, isOptimistic: false, currentValue: false };
    }
    
    const movie = movieLookup[tmdbId];
    
    if (!movie) {
      console.warn(`🔍 [LOOKUP] Movie ${tmdbId} not found in lookup`, {
        lookupSize: Object.keys(movieLookup).length,
        propertyName,
        availableIds: Object.keys(movieLookup).slice(0, 5).map(Number)
      });
      
      // FALLBACK: Search in allMovies array directly
      const fallbackMovie = allMovies.find(m => m.tmdbId === tmdbId);
      if (fallbackMovie) {
        console.log(`🔍 [LOOKUP] Found movie in fallback search: ${fallbackMovie.title}`);
        return { isPending: false, isOptimistic: false, currentValue: fallbackMovie[propertyName] };
      }
      
      return { isPending: false, isOptimistic: false, currentValue: false };
    }
    
    const shouldLog = isDebugMovie(movie);
    
    const pendingAction = pendingActions.find(action => {
      return action.movieId === tmdbId && action.type === propertyName;
    });
    
    if (shouldLog) {
      console.log(`🔍 [LOOKUP] Visual state for "${movie.title}" ${propertyName}:`, {
        tmdbId,
        actualState: movie[propertyName],
        hasPendingAction: !!pendingAction,
        pendingActionId: pendingAction?.id
      });
    }
    
    if (pendingAction) {
      const now = Date.now();
      const isExpired = now - pendingAction.timestamp >= 3000;
      
      if (isExpired) {
        return { isPending: false, isOptimistic: false, currentValue: movie[propertyName] };
      }
      
      // Check if this is an undo scenario
      const isUndoScenario = 
        (pendingAction.fromSection === LIST_NAMES.FAVORITES && propertyName === 'favorite') ||
        (pendingAction.fromSection === LIST_NAMES.WATCHLIST && propertyName === 'planToWatch') ||
        (pendingAction.fromSection === LIST_NAMES.UNSEEN_GEMS && propertyName === 'favorite');
      
      if (isUndoScenario) {
        return { isPending: true, isOptimistic: false, currentValue: movie[propertyName] };
      } else {
        const optimisticValue = !movie[propertyName];
        return { isPending: true, isOptimistic: true, currentValue: optimisticValue };
      }
    }
    
    return { isPending: false, isOptimistic: false, currentValue: movie[propertyName] };
  }, [movieLookup, pendingActions, allMovies]);
  
  // Enhanced pending action check with fallback
  const isPendingAction = useCallback((
    tmdbId: number, 
    type: 'favorite' | 'planToWatch'
  ): boolean => {
    if (!tmdbId || tmdbId <= 0) {
      return false;
    }
    
    const movie = movieLookup[tmdbId];
    if (!movie) {
      // Try fallback search
      const fallbackMovie = allMovies.find(m => m.tmdbId === tmdbId);
      if (!fallbackMovie) {
        return false;
      }
    }
    
    return pendingActions.some(action => {
      const isMatchingAction = action.movieId === tmdbId && action.type === type;
      const now = Date.now();
      const isExpired = now - action.timestamp >= 3000;
      
      return isMatchingAction && !isExpired;
    });
  }, [pendingActions, movieLookup, allMovies]);

  // Get pending action with validation
  const getPendingAction = useCallback((tmdbId: number, actionType: 'favorite' | 'planToWatch') => {
    if (!tmdbId || tmdbId <= 0) {
      return undefined;
    }
    
    return pendingActions.find(action => 
      action.movieId === tmdbId && action.type === actionType
    );
  }, [pendingActions]);

  return {
    movieLookup,
    getVisualState,
    isPendingAction,
    getPendingAction
  };
};
